import { Button, CardHorizontalDivider, Flex, Grid, Status } from "@eworkit/iris";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { EmptyText } from "@/components/EmptyText";
import { Group } from "@/components/Group";
import { ISupplierBankingDetails } from "@/data/companies";
import { Header, Text } from "@/eworkitComponents";
import { PAYMENT_NAMES } from "@/views/Settings/CompanySettings/BankingDetails/validationSchema";

interface ICompanyBankingDetailsPreviewProps {
  details?: ISupplierBankingDetails[];
  makePrimary: (data: ISupplierBankingDetails) => Promise<void>;
}

export const getBankAccountLabel = (detail: ISupplierBankingDetails) =>
  detail?.paymentName === PAYMENT_NAMES.DOMESTIC_BANKACCOUNT_TRANSACTION ? "Bank account" : detail?.paymentName;

export const CompanyBankingDetailsPreview = ({ details, makePrimary }: ICompanyBankingDetailsPreviewProps) => {
  const { t } = useTranslation("session");

  const isFactoringCompany = (bankDetail: ISupplierBankingDetails) => (bankDetail.factoringCompany ? "Yes" : "No");

  return (
    <Flex flexDirection="column" gap="md">
      {details && details.length ? (
        details.map(bankDetail => (
          <Fragment key={bankDetail.id}>
            <Header size="xs" weight="bold" sx={{ display: "flex" }}>
              <Flex gap="lg" justifyContent="flex-start" alignItems="center">
                {bankDetail.primary ? (
                  <Status variant="info" size="lg">
                    {t("USERS.SETTINGS.BANKING_DETAILS.FORM.PRIMARY")}
                  </Status>
                ) : (
                  <Button size="sm" onClick={async () => makePrimary(bankDetail)}>
                    {t("USERS.SETTINGS.BANKING_DETAILS.FORM.MAKE_PRIMARY")}
                  </Button>
                )}
              </Flex>
            </Header>

            <Grid columns="1fr 1fr">
              <Group
                label={t("USERS.SETTINGS.BANKING_DETAILS.FORM.COUNTRY")}
                direction="column"
                sx={{ width: "300px" }}>
                <Text size="md">{bankDetail.countryCode ? bankDetail.countryCode : <EmptyText />}</Text>
              </Group>

              <Group label={t("USERS.SETTINGS.BANKING_DETAILS.FORM.TYPE")} direction="column">
                <Text size="md">{bankDetail.paymentName ? bankDetail.paymentName : <EmptyText />} </Text>
              </Group>

              <Group label={t("USERS.SETTINGS.BANKING_DETAILS.FORM.FACTORING_COMPANY")} direction="column">
                <Text size="md">
                  {typeof bankDetail.factoringCompany !== "undefined" ? isFactoringCompany(bankDetail) : <EmptyText />}
                </Text>
              </Group>

              {bankDetail.bankNumber && (
                <Group
                  label={t("USERS.SETTINGS.BANKING_DETAILS.FORM.BANK_NUMBER", {
                    value: getBankAccountLabel(bankDetail),
                  })}
                  direction="column">
                  <Text size="md">{bankDetail.bankNumber ? bankDetail.bankNumber : <EmptyText />}</Text>
                </Group>
              )}

              {bankDetail.swift && (
                <Group label={t("USERS.SETTINGS.BANKING_DETAILS.FORM.SWIFT")} direction="column">
                  <Text size="md">{bankDetail.swift ? bankDetail.swift : <EmptyText />}</Text>
                </Group>
              )}

              {bankDetail.iban && (
                <Group label={t("USERS.SETTINGS.BANKING_DETAILS.FORM.IBAN")} direction="column">
                  <Text size="md">{bankDetail.iban ? bankDetail.iban : <EmptyText />}</Text>
                </Group>
              )}
            </Grid>

            {details.indexOf(bankDetail) !== details.length - 1 && <CardHorizontalDivider />}
          </Fragment>
        ))
      ) : (
        <Text size="md">{t("USERS.SETTINGS.BANKING_DETAILS.FORM.NO_DETAILS")}</Text>
      )}
    </Flex>
  );
};
